import { isEqualShallow } from '../../isEqualShallow';
import type { ObservableState } from '../observable.types';

/**
 * Allows subscribing to a slice of the state.
 * The slice is a computation of the states.
 *
 * The callback will only be called when the slice has changed.
 * Change is determined by shallow comparison.
 *
 * Returns a function to unsubscribe the callback.
 */
export const subscribeWithSelector =
  <T extends Record<string, unknown>, Slice>(
    observable: ObservableState<T>,
    selector: (value: T) => Slice
  ) =>
  (callback: (slice: Slice) => void) => {
    let lastSlice = selector(observable.get());

    return observable.subscribe((value) => {
      const nextSlice = selector(value);

      if (isEqualShallow(nextSlice, lastSlice)) return;

      lastSlice = nextSlice;

      callback(nextSlice);
    });
  };
